// src/composables/useWebPush.js
// Web push (FCM) for the installed PWA. Requests notification permission, gets an
// FCM token, and stores it in users/{uid}/pushTokens — the same place the backend's
// sendPushNotification reads. Once a token is registered, every alert that already
// fans out through deliverAlert pushes to the phone. Foreground messages surface as
// an in-app toast. Requires VUE_APP_FIREBASE_VAPID_KEY (Firebase console → Project
// Settings → Cloud Messaging → Web Push certificates → Generate key pair).

import { ref } from 'vue'
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging'
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { firebaseApp, db } from '@/services/firebaseconfig'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/composables/useToast'
import router from '@/router'

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY || ''
let messagingInstance = null
let foregroundAttached = false

// Deterministic doc id per token (so re-registering the same token updates one doc).
function tokenDocId(token) {
  let h = 5381
  for (let i = 0; i < token.length; i += 1) h = ((h << 5) + h + token.charCodeAt(i)) >>> 0
  return `web_${h.toString(36)}`
}

// Tap target for a foreground push toast — deep-link to the /m destination the
// backend put in data.url (same-origin → SPA push; otherwise open a tab).
function navigateFromPush(url) {
  if (!url) return
  try {
    const u = new URL(url, window.location.origin)
    if (u.origin === window.location.origin) router.push(u.pathname + u.search)
    else window.open(url, '_blank', 'noopener')
  } catch {
    /* ignore */
  }
}

export function useWebPush() {
  const userStore = useUserStore()
  const toast = useToast()
  const permission = ref(typeof Notification !== 'undefined' ? Notification.permission : 'denied')

  async function supported() {
    try {
      return (
        typeof Notification !== 'undefined' && 'serviceWorker' in navigator && (await isSupported())
      )
    } catch {
      return false
    }
  }

  function messaging() {
    if (!messagingInstance) {
      messagingInstance = getMessaging(firebaseApp)
      if (!foregroundAttached) {
        foregroundAttached = true
        // App-in-foreground pushes don't hit the SW — surface them as a toast.
        onMessage(messagingInstance, payload => {
          const n = payload?.notification || {}
          const text = `${n.title ? `${n.title} — ` : ''}${n.body || ''}`.trim()
          if (!text) return
          const url = payload?.data?.url || payload?.fcmOptions?.link || ''
          toast.info(text, url ? { onClick: () => navigateFromPush(url) } : undefined)
        })
      }
    }
    return messagingInstance
  }

  // FCM auto-registers /firebase-messaging-sw.js (its own scope, separate from
  // the PWA /service-worker.js).
  async function fetchToken() {
    if (!VAPID_KEY) {
      console.warn('[push] VUE_APP_FIREBASE_VAPID_KEY not set — web push disabled')
      return null
    }
    return (await getToken(messaging(), { vapidKey: VAPID_KEY })) || null
  }

  async function storeToken(token) {
    const uid = userStore.user?.uid
    if (!uid || !token) return
    await setDoc(
      doc(db, `users/${uid}/pushTokens`, tokenDocId(token)),
      {
        token,
        platform: 'web',
        userAgent: navigator.userAgent || '',
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
  }

  // User-gesture entry (banner button): request permission + register the token.
  async function enable() {
    if (!(await supported())) {
      toast.error('Notifications aren’t supported here.')
      return false
    }
    let perm
    try {
      perm = await Notification.requestPermission()
    } catch {
      perm = 'denied'
    }
    permission.value = perm
    if (perm !== 'granted') return false
    try {
      const token = await fetchToken()
      if (!token) {
        toast.error('Could not enable notifications.')
        return false
      }
      await storeToken(token)
      // Route pushes to this device by flipping the master push preference on —
      // the same `channels.push` the desktop alert system + deliverAlert gate on,
      // so opting in here (or via Settings) enables every push path at once.
      try {
        const uid = userStore.user?.uid
        if (uid) {
          await setDoc(
            doc(db, `users/${uid}/alertPreferences/default`),
            { channels: { push: true }, _version: 3, _updatedAt: serverTimestamp() },
            { merge: true }
          )
        }
      } catch (e) {
        console.warn('[push] could not enable push preference', e)
      }
      toast.success('Notifications are on.')
      return true
    } catch (e) {
      console.warn('[push] enable failed', e)
      toast.error('Could not enable notifications.')
      return false
    }
  }

  // Silent refresh on app open if already granted (keeps the token current).
  async function syncIfGranted() {
    try {
      if (!userStore.user?.uid) return
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
      if (!(await supported())) return
      const token = await fetchToken()
      if (token) await storeToken(token)
    } catch {
      /* non-fatal */
    }
  }

  async function disable() {
    try {
      const token = await fetchToken()
      if (token) {
        const uid = userStore.user?.uid
        if (uid)
          await deleteDoc(doc(db, `users/${uid}/pushTokens`, tokenDocId(token))).catch(() => {})
      }
      await deleteToken(messaging()).catch(() => {})
      permission.value = typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    } catch {
      /* ignore */
    }
  }

  return { permission, supported, enable, syncIfGranted, disable }
}
